import React from "react";

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Split text and wrap case-insensitive phrase matches in <mark>. */
export function highlightPhrasesInText(
  text: string,
  phrases: string[] | undefined | null
): React.ReactNode {
  const cleaned = (phrases ?? [])
    .map((phrase) => phrase?.trim())
    .filter((phrase): phrase is string => !!phrase && phrase.length >= 2);

  if (!text || cleaned.length === 0) {
    return text;
  }

  // Longer phrases first so nested/overlapping matches prefer the fuller quote.
  const unique = Array.from(new Set(cleaned)).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(${unique.map(escapeRegExp).join("|")})`, "gi");
  const parts = text.split(pattern);

  if (parts.length === 1) {
    return text;
  }

  return parts.map((part, index) => {
    const isMatch = unique.some(
      (phrase) => part.toLocaleLowerCase() === phrase.toLocaleLowerCase()
    );
    if (!isMatch) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }

    return (
      <mark key={index} className="analyse-search-highlight">
        {part}
      </mark>
    );
  });
}

export function highlightPhrasesInChildren(
  children: React.ReactNode,
  phrases: string[] | undefined | null
): React.ReactNode {
  if (!phrases?.length) {
    return children;
  }

  return React.Children.map(children, (child) => {
    if (typeof child === "string") {
      return highlightPhrasesInText(child, phrases);
    }

    if (typeof child === "number") {
      return child;
    }

    if (!React.isValidElement(child)) {
      return child;
    }

    const element = child as React.ReactElement<{ children?: React.ReactNode }>;
    if (element.props.children == null) {
      return child;
    }

    return React.cloneElement(element, {
      ...element.props,
      children: highlightPhrasesInChildren(element.props.children, phrases),
    });
  });
}
